import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Shield, MapPin, User, Shirt, CreditCard } from "lucide-react";

const money = (c, cur = "CAD") => `$${((c || 0) / 100).toFixed(0)} ${cur}`;
const POS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "OF", "UTIL"];
const inp = "w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white";
const lbl = "block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1";

export default function WwbaRegister() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const sessionId = params.get("session_id");
  const canceled = params.get("canceled");

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(!!sessionId);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/wwba/register/${token}`);
      setData(data);
      setF({
        ...data.prefill,
        address: { line1: "", line2: "", city: "", region: "", postal_code: "", country: "Canada", ...(data.address || {}) },
        parent1: { name: "", cell: "", email: "", ...(data.parent1 || {}) },
        parent2: { name: "", cell: "", email: "", ...(data.parent2 || {}) },
        uniform: { shirt_size: "", jersey_size: "", hat_size: "", jersey_choice_1: "", jersey_choice_2: "", ...(data.uniform || {}) },
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || "This registration link is invalid.");
    }
  }, [token]);

  useEffect(() => {
    if (!token) { setErr("Missing registration token."); return; }
    if (canceled) toast.info("Payment canceled — your registration is saved. You can pay the deposit anytime.");
    if (sessionId) {
      // poll payment status
      let tries = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/wwba/register/${token}/status/${sessionId}`);
          if (data.deposit_paid) { setData((d) => ({ ...(d || {}), ...pubShape(data) })); setConfirming(false); return; }
        } catch { /* ignore */ }
        if (++tries < 8) setTimeout(poll, 1500); else { setConfirming(false); load(); }
      };
      poll();
    } else {
      load();
    }
  }, [token, sessionId, canceled, load]);

  const pubShape = (s) => ({ registration_status: s.registration_status, deposit_paid: s.deposit_paid, pricing: { total_cents: s.total_cost_cents, deposit_cents: s.deposit_amount_cents, balance_cents: s.balance_remaining_cents, currency: s.currency } });

  const setU = (k, v) => setF((p) => ({ ...p, uniform: { ...p.uniform, [k]: v } }));
  const setA = (k, v) => setF((p) => ({ ...p, address: { ...p.address, [k]: v } }));
  const setP = (n, k, v) => setF((p) => ({ ...p, [n]: { ...p[n], [k]: v } }));

  const save = async () => {
    if (!f.first_name || !f.last_name) return toast.error("First and last name are required");
    if (!f.primary_position) return toast.error("Primary position is required");
    if (!f.uniform.shirt_size || !f.uniform.jersey_size || !f.uniform.hat_size) return toast.error("All uniform sizes are required");
    if (!f.uniform.jersey_choice_1 || !f.uniform.jersey_choice_2) return toast.error("Both jersey number choices are required");
    if (f.uniform.jersey_choice_1 === f.uniform.jersey_choice_2) return toast.error("Jersey number choices must be different");
    if (!f.parent1.name || !f.parent1.cell || !f.parent1.email) return toast.error("Parent/Guardian #1 name, cell and email are required");
    if (!f.address.line1 || !f.address.city || !f.address.region || !f.address.postal_code || !f.address.country) return toast.error("Complete mailing address is required");
    setBusy(true);
    try { await api.post(`/wwba/register/${token}/save`, f); return true; }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); return false; }
    finally { setBusy(false); }
  };

  const payDeposit = async () => {
    const ok = await save();
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/wwba/register/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment"); setBusy(false); }
  };

  if (err) return <Shell><div data-testid="wwba-error" className="text-center py-20 text-slate-600">{err}</div></Shell>;
  if (confirming) return <Shell><div className="text-center py-20"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#E63946]" /><p className="mt-3 text-slate-500">Confirming your payment…</p></div></Shell>;
  if (!data || !f) return <Shell><div className="text-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div></Shell>;

  const price = data.pricing;
  if (data.deposit_paid) return <Confirmed data={data} />;

  const opt = data.uniform_options;
  return (
    <Shell event={data.event}>
      <PriceBanner price={price} />
      <div className="max-w-3xl mx-auto px-4 space-y-6 pb-16">
        {/* Player */}
        <Card icon={User} title="Player Information">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field l="First Name *"><input data-testid="wwba-first" className={inp} value={f.first_name || ""} onChange={(e) => setF({ ...f, first_name: e.target.value })} /></Field>
            <Field l="Last Name *"><input data-testid="wwba-last" className={inp} value={f.last_name || ""} onChange={(e) => setF({ ...f, last_name: e.target.value })} /></Field>
            <Field l="Date of Birth *"><input type="date" className={inp} value={f.date_of_birth || ""} onChange={(e) => setF({ ...f, date_of_birth: e.target.value })} /></Field>
            <Field l="Player Cell Number *"><input data-testid="wwba-cell" className={inp} value={f.player_cell || ""} onChange={(e) => setF({ ...f, player_cell: e.target.value })} /></Field>
            <Field l="Primary Position *"><select data-testid="wwba-pos1" className={inp} value={f.primary_position || ""} onChange={(e) => setF({ ...f, primary_position: e.target.value })}><option value="">Select…</option>{POS.map((p) => <option key={p}>{p}</option>)}</select></Field>
            <Field l="Secondary Position"><select className={inp} value={f.secondary_position || ""} onChange={(e) => setF({ ...f, secondary_position: e.target.value })}><option value="">None</option>{POS.map((p) => <option key={p}>{p}</option>)}</select></Field>
            <Field l="Height *"><input className={inp} placeholder={`6'2"`} value={f.height || ""} onChange={(e) => setF({ ...f, height: e.target.value })} /></Field>
            <Field l="Weight *"><input className={inp} placeholder="215 lbs" value={f.weight || ""} onChange={(e) => setF({ ...f, weight: e.target.value })} /></Field>
            <Field l="Current Team *"><input className={inp} value={f.current_team || ""} onChange={(e) => setF({ ...f, current_team: e.target.value })} /></Field>
            <Field l="Current High School *"><input className={inp} value={f.high_school || ""} onChange={(e) => setF({ ...f, high_school: e.target.value })} /></Field>
          </div>
        </Card>

        {/* Address */}
        <Card icon={MapPin} title="Mailing Address">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field l="Address Line 1 *" full><input className={inp} value={f.address.line1} onChange={(e) => setA("line1", e.target.value)} /></Field>
            <Field l="Address Line 2" full><input className={inp} value={f.address.line2} onChange={(e) => setA("line2", e.target.value)} /></Field>
            <Field l="City *"><input className={inp} value={f.address.city} onChange={(e) => setA("city", e.target.value)} /></Field>
            <Field l="Province/State *"><input className={inp} value={f.address.region} onChange={(e) => setA("region", e.target.value)} /></Field>
            <Field l="Postal/ZIP Code *"><input className={inp} value={f.address.postal_code} onChange={(e) => setA("postal_code", e.target.value)} /></Field>
            <Field l="Country *"><input className={inp} value={f.address.country} onChange={(e) => setA("country", e.target.value)} /></Field>
          </div>
        </Card>

        {/* Parents */}
        <Card icon={Shield} title="Parent / Guardian">
          <div className="grid sm:grid-cols-3 gap-3">
            <Field l="Guardian #1 Name *"><input data-testid="wwba-p1-name" className={inp} value={f.parent1.name} onChange={(e) => setP("parent1", "name", e.target.value)} /></Field>
            <Field l="Guardian #1 Cell *"><input className={inp} value={f.parent1.cell} onChange={(e) => setP("parent1", "cell", e.target.value)} /></Field>
            <Field l="Guardian #1 Email *"><input data-testid="wwba-p1-email" className={inp} value={f.parent1.email} onChange={(e) => setP("parent1", "email", e.target.value)} /></Field>
            <Field l="Guardian #2 Name"><input className={inp} value={f.parent2.name} onChange={(e) => setP("parent2", "name", e.target.value)} /></Field>
            <Field l="Guardian #2 Cell"><input className={inp} value={f.parent2.cell} onChange={(e) => setP("parent2", "cell", e.target.value)} /></Field>
            <Field l="Guardian #2 Email"><input className={inp} value={f.parent2.email} onChange={(e) => setP("parent2", "email", e.target.value)} /></Field>
          </div>
        </Card>

        {/* Uniform */}
        <Card icon={Shirt} title="CBR Nation Uniform Information">
          <div className="grid sm:grid-cols-3 gap-3">
            <Field l="Shirt Size *"><select data-testid="wwba-shirt" className={inp} value={f.uniform.shirt_size} onChange={(e) => setU("shirt_size", e.target.value)}><option value="">Select…</option>{opt.shirt_sizes.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field l="Jersey Size *"><select data-testid="wwba-jsize" className={inp} value={f.uniform.jersey_size} onChange={(e) => setU("jersey_size", e.target.value)}><option value="">Select…</option>{opt.jersey_sizes.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field l="Hat Size *"><select data-testid="wwba-hat" className={inp} value={f.uniform.hat_size} onChange={(e) => setU("hat_size", e.target.value)}><option value="">Select…</option>{opt.hat_sizes.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field l="Jersey Number — Choice #1 *"><input data-testid="wwba-j1" type="number" className={inp} value={f.uniform.jersey_choice_1} onChange={(e) => setU("jersey_choice_1", e.target.value)} /></Field>
            <Field l="Jersey Number — Choice #2 *"><input data-testid="wwba-j2" type="number" className={inp} value={f.uniform.jersey_choice_2} onChange={(e) => setU("jersey_choice_2", e.target.value)} /></Field>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Jersey numbers are requested but not guaranteed. CBR Nation will make every effort to accommodate player preferences.</p>
        </Card>

        <div className="flex flex-col sm:flex-row gap-3">
          <button data-testid="wwba-save" onClick={save} disabled={busy} className="flex-1 border border-[#0A192F] text-[#0A192F] font-semibold uppercase tracking-wider text-sm py-3 rounded-sm disabled:opacity-50">Save Progress</button>
          <button data-testid="wwba-pay" onClick={payDeposit} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#E63946] text-white font-semibold uppercase tracking-wider text-sm py-3 rounded-sm disabled:opacity-50">
            <CreditCard className="w-4 h-4" /> {busy ? "Please wait…" : `Pay ${money(price.deposit_cents, price.currency)} Deposit`}
          </button>
        </div>
      </div>
    </Shell>
  );
}

const Shell = ({ children, event }) => (
  <div className="min-h-screen bg-slate-50">
    <div className="bg-[#0A192F] text-white py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="label-overline text-[#E63946]">CBR Nation</div>
        <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-1">WWBA Registration</h1>
        {event && <p className="text-sm text-white/60 mt-1">{event.location} · {event.start_date} – {event.end_date}</p>}
      </div>
    </div>
    <div className="pt-8">{children}</div>
  </div>
);

const PriceBanner = ({ price }) => (
  <div className="max-w-3xl mx-auto px-4 mb-6">
    <div data-testid="wwba-price-banner" className="grid grid-cols-3 gap-3 bg-white border border-slate-200 rounded-sm p-4 text-center">
      <div><div className="text-[11px] uppercase tracking-wider text-slate-400">Total Event Cost</div><div className="font-data text-xl text-[#0A192F]">{money(price.total_cents, price.currency)}</div></div>
      <div className="border-x border-slate-100"><div className="text-[11px] uppercase tracking-wider text-slate-400">Deposit Due Today</div><div className="font-data text-xl text-[#E63946]">{money(price.deposit_cents, price.currency)}</div></div>
      <div><div className="text-[11px] uppercase tracking-wider text-slate-400">Remaining Balance</div><div className="font-data text-xl text-slate-600">{money(price.balance_cents, price.currency)}</div></div>
    </div>
  </div>
);

const Card = ({ icon: Icon, title, children }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-5">
    <div className="flex items-center gap-2 mb-4"><Icon className="w-4 h-4 text-[#E63946]" /><span className="label-overline">{title}</span></div>
    {children}
  </div>
);
const Field = ({ l, children, full }) => <div className={full ? "sm:col-span-2" : ""}><label className={lbl}>{l}</label>{children}</div>;

function Confirmed({ data }) {
  const p = data.pricing || {};
  return (
    <Shell event={data.event}>
      <div className="max-w-xl mx-auto px-4 text-center pb-20" data-testid="wwba-confirmed">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
        <h2 className="font-display text-3xl uppercase tracking-tight mt-4">You're Registered</h2>
        <p className="text-slate-500 mt-1">CBR Nation — WWBA Registration</p>
        <p className="text-slate-500">{data.event?.location} · {data.event?.start_date} – {data.event?.end_date}</p>
        <div className="grid grid-cols-2 gap-3 mt-6">
          <div className="bg-white border border-slate-200 rounded-sm p-4"><div className="text-[11px] uppercase tracking-wider text-slate-400">Deposit Paid</div><div className="font-data text-2xl text-emerald-600">{money(p.deposit_cents, p.currency)}</div></div>
          <div className="bg-white border border-slate-200 rounded-sm p-4"><div className="text-[11px] uppercase tracking-wider text-slate-400">Remaining Balance</div><div className="font-data text-2xl text-slate-600">{money(p.balance_cents, p.currency)}</div></div>
        </div>
        <p className="text-sm text-slate-500 mt-6">Additional trip information and instructions for paying your remaining balance will be provided later.</p>
        <a href="/dashboard" className="inline-block mt-6 bg-[#0A192F] text-white font-semibold uppercase tracking-wider text-sm px-6 py-3 rounded-sm">View My CBR Prospects Profile</a>
      </div>
    </Shell>
  );
}
